// The "Your team" step (overhaul §6.2, panel 03) — the self-vs-invite fork. A self-signup
// gets the invite/join options; a user who arrived via an invite link (a pending invite
// exists) gets the "you're joining {Team}" accept flow. Reuses the real team hooks
// (useSendInvite / useAcceptInvite — the same mutations the People surface + GlobalInviteBanner
// use). Team is NOT required to capture, so "Continue" always skips ahead. Copy verbatim from
// the mock.

import { useState } from 'react';
import {
  usePendingInvites,
  useSendInvite,
  useAcceptInvite,
  useEnsureTeam,
  type PendingInvite,
} from '../../hooks/useTeamSettings';

// Leading round icon for a .team-opt (the mock inline-styles these — no dedicated class).
function OptIcon({ glyph, accent }: { glyph: string; accent?: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        width: 30, height: 30, borderRadius: 8, flex: 'none',
        display: 'grid', placeItems: 'center', fontWeight: 700,
        background: accent ? 'var(--acc-soft)' : 'var(--card)',
        color: accent ? 'var(--acc)' : 'var(--inkS)',
        border: accent ? 'none' : '1px solid var(--hair2)',
      }}
    >
      {glyph}
    </span>
  );
}

function SelfBranch({ onContinue, onBack }: {
  onContinue: () => void;
  onBack: () => void;
}) {
  const sendInvite = useSendInvite();
  // bug-448: a first-run self-signup has no team yet, so we can't gate the invite on an
  // existing teamId (that left the field permanently disabled). Instead, ensure a team
  // exists — creating one on the first invite — then send into it.
  const { ensure, isPending: creatingTeam } = useEnsureTeam();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const busy = sendInvite.isPending || creatingTeam;

  const send = async () => {
    const target = email.trim();
    if (!target) return;
    setErr(null);
    try {
      const teamId = await ensure();
      await sendInvite.mutateAsync({ teamId, email: target });
      setSent((s) => [...s, target]);
      setEmail('');
    } catch {
      setErr("Couldn't send that invite — check the address and try again.");
    }
  };

  return (
    <>
      <div className="crumb">STEP 03 · YOUR TEAM</div>
      <div className="wh">Bring your <span className="em">team</span>.</div>
      <p className="wp">
        The shared graph is the whole point — it sharpens when teammates' context joins yours.
        Invite a few now, or join an existing team instead.
      </p>

      <div className="team-opt" style={{ alignItems: 'flex-start' }}>
        <OptIcon glyph="+" accent />
        <div className="to-main">
          <div className="to-t">Invite members to your team</div>
          <div className="to-s">Send invite links — they sign up straight into your team.</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <input
              type="email"
              placeholder="teammate@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void send(); }}
              disabled={busy}
              style={{
                flex: 1, padding: '8px 12px', borderRadius: 8,
                border: '1px solid var(--hair2)', background: 'var(--card)',
                color: 'var(--ink)', fontSize: 13,
              }}
            />
            <button
              className="btn btn-primary sm"
              onClick={send}
              disabled={!email.trim() || busy}
            >
              {busy ? 'Sending…' : 'Send invite'}
            </button>
          </div>
          {sent.length > 0 && (
            <div className="to-s" style={{ color: 'var(--grn)', marginTop: 6 }}>Invited {sent.join(', ')} ✓</div>
          )}
          {err && <div className="to-s" style={{ color: 'var(--rose)', marginTop: 6 }}>{err}</div>}
        </div>
      </div>

      <div className="team-opt" style={{ alignItems: 'flex-start' }}>
        <OptIcon glyph="↪" />
        <div className="to-main">
          <div className="to-t">Join an existing team instead</div>
          <div className="to-s">
            Ask that team's admin for an invite link, then open it — it brings you straight here
            to accept.
          </div>
        </div>
      </div>

      <div className="wiz-foot">
        <button className="btn btn-secondary" onClick={onBack}>Back</button>
        <button className="btn btn-primary" onClick={onContinue}>Continue <span className="arr">→</span></button>
        <span className="prog">Step 3 of 4</span>
      </div>
    </>
  );
}

function InviteBranch({ invite, onContinue, onBack }: {
  invite: PendingInvite;
  onContinue: () => void;
  onBack: () => void;
}) {
  const accept = useAcceptInvite();
  const [err, setErr] = useState<string | null>(null);

  const join = async () => {
    setErr(null);
    try {
      await accept.mutateAsync({ teamId: invite.team_id, inviteId: invite.id });
      onContinue();
    } catch {
      setErr("Couldn't join right now — please try again.");
    }
  };

  return (
    <>
      <div className="crumb">STEP 03 · YOUR TEAM</div>
      <div className="wh">You're joining <span className="em">{invite.team_name}</span>.</div>
      <p className="wp">
        {invite.invited_by_email} invited you. Your context joins their shared graph — your
        private conversations stay private.
      </p>
      <div className="wiz-foot">
        <button className="btn btn-secondary" onClick={onBack}>Back</button>
        <button className="btn btn-primary" onClick={join} disabled={accept.isPending}>
          {accept.isPending ? 'Joining…' : <>Join {invite.team_name} <span className="arr">→</span></>}
        </button>
        {err && <span style={{ fontSize: 13, color: 'var(--rose)' }}>{err}</span>}
        <span className="prog">Step 3 of 4</span>
      </div>
    </>
  );
}

export function TeamStep({ onContinue, onBack }: { onContinue: () => void; onBack: () => void }) {
  const { data: pending } = usePendingInvites();
  const invite = pending?.invites?.[0] ?? null;

  if (invite) {
    return <InviteBranch invite={invite} onContinue={onContinue} onBack={onBack} />;
  }
  return <SelfBranch onContinue={onContinue} onBack={onBack} />;
}
